/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useRef, useState, useCallback } from "react";
import {
	Box,
	TextField,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	FormControlLabel,
	Checkbox,
	Button,
	IconButton,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { MobileMetadata, Reminder } from "../../domain/treatmentPlan.interface";

const TEMPLATE_VARIABLES = [
	{ label: "First Name", value: "{firstName}" },
	{ label: "Event Name", value: "{eventName}" },
	{ label: "Day Number", value: "{dayNumber}" },
	{ label: "Incentive Amount", value: "{incentiveAmount}" },
];

const SAMPLE_DATA: Record<string, string> = {
	"{firstName}": "John",
	"{eventName}": "Urine Drug Test",
	"{dayNumber}": "5",
	"{incentiveAmount}": "$25.00",
};

const MAX_REMINDERS = 3;

const DEFAULT_REMINDER: Reminder = {
	intervalMinutes: 30,
	message: "Reminder: Don't forget to complete your visit today!",
};

interface CharacterCounterFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
	maxLength: number;
	required?: boolean;
	multiline?: boolean;
	rows?: number;
	inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
	helperText?: string;
}

const CharacterCounterField: FC<CharacterCounterFieldProps> = ({
	label,
	value,
	onChange,
	maxLength,
	required = false,
	multiline = false,
	rows,
	inputRef,
	helperText,
}) => (
	<Box sx={{ position: "relative" }}>
		<TextField
			fullWidth
			label={label}
			value={value}
			onChange={(e) => onChange(e.target.value)}
			required={required}
			multiline={multiline}
			rows={rows}
			inputProps={{ maxLength }}
			inputRef={inputRef}
			helperText={helperText}
		/>
		<Box sx={{ position: "absolute", right: 10, bottom: helperText ? 30 : 10 }}>
			<Typography variant="caption" color="text.secondary">
				{value.length}/{maxLength}
			</Typography>
		</Box>
	</Box>
);

const replaceTemplateVars = (text: string): string => {
	let result = text;
	for (const [key, val] of Object.entries(SAMPLE_DATA)) {
		result = result.replaceAll(key, val);
	}
	return result;
};

interface InsertVariableButtonProps {
	inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
	value: string;
	onChange: (value: string) => void;
}

const InsertVariableButton: FC<InsertVariableButtonProps> = ({
	inputRef,
	value,
	onChange,
}) => {
	const handleInsert = (variable: string) => {
		const el = inputRef.current;
		if (el) {
			const start = el.selectionStart ?? value.length;
			const end = el.selectionEnd ?? value.length;
			const newValue =
				value.substring(0, start) + variable + value.substring(end);
			onChange(newValue);
			// Restore cursor position after React re-render
			setTimeout(() => {
				el.focus();
				const newCursor = start + variable.length;
				el.setSelectionRange(newCursor, newCursor);
			}, 0);
		} else {
			onChange(value + variable);
		}
	};

	return (
		<FormControl size="small" sx={{ minWidth: 160 }}>
			<InputLabel>Insert Variable</InputLabel>
			<Select
				value=""
				onChange={(e) => {
					if (e.target.value) handleInsert(e.target.value as string);
				}}
				label="Insert Variable"
			>
				{TEMPLATE_VARIABLES.map((v) => (
					<MenuItem key={v.value} value={v.value}>
						{v.label} — <code>{v.value}</code>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

interface MobileMetadataSectionProps {
	mobileMetadata?: MobileMetadata;
	onChange: (mobileMetadata: MobileMetadata | undefined) => void;
	eventName?: string;
}

const MobileMetadataSection: FC<MobileMetadataSectionProps> = ({
	mobileMetadata,
	onChange,
}) => {
	const [expanded, setExpanded] = useState(
		() => sessionStorage.getItem("mobile-config-expanded") === "true",
	);

	const titleRef = useRef<HTMLInputElement>(null);
	const bodyRef = useRef<HTMLTextAreaElement>(null);
	const instructionsRef = useRef<HTMLTextAreaElement>(null);
	const successRef = useRef<HTMLInputElement>(null);

	const handleExpand = useCallback(
		(_: React.SyntheticEvent, isExpanded: boolean) => {
			setExpanded(isExpanded);
			sessionStorage.setItem("mobile-config-expanded", String(isExpanded));
		},
		[],
	);

	const metadata: MobileMetadata = mobileMetadata ?? {
		notificationTitle: "",
		notificationBody: "",
		successMessage: "",
	};

	const updateField = (field: keyof MobileMetadata, value: any) => {
		onChange({ ...metadata, [field]: value });
	};

	const reminders = metadata.reminderConfig?.reminders ?? [];
	const remindersEnabled = metadata.reminderConfig?.enabled ?? false;

	const updateReminder = (index: number, updates: Partial<Reminder>) => {
		const updated = [...reminders];
		updated[index] = { ...updated[index], ...updates };
		updateField("reminderConfig", {
			enabled: remindersEnabled,
			reminders: updated,
		});
	};

	const addReminder = () => {
		if (reminders.length >= MAX_REMINDERS) return;
		updateField("reminderConfig", {
			enabled: remindersEnabled,
			reminders: [...reminders, { ...DEFAULT_REMINDER }],
		});
	};

	const removeReminder = (index: number) => {
		const updated = reminders.filter((_, i) => i !== index);
		updateField("reminderConfig", {
			enabled: remindersEnabled,
			reminders: updated,
		});
	};

	const toggleReminders = (enabled: boolean) => {
		updateField("reminderConfig", {
			enabled,
			reminders:
				enabled && reminders.length === 0
					? [{ ...DEFAULT_REMINDER }]
					: reminders,
		});
	};

	// Cumulative timing helper
	const getCumulativeTiming = (index: number): string => {
		let total = 0;
		for (let i = 0; i <= index; i++) {
			total += reminders[i]?.intervalMinutes ?? 0;
		}
		if (total < 60) return `${total} min after notification`;
		const hours = Math.floor(total / 60);
		const mins = total % 60;
		return mins > 0
			? `${hours}h ${mins}m after notification`
			: `${hours}h after notification`;
	};

	const formatMinutesDisplay = (minutes: number): string => {
		if (minutes < 60) return `${minutes} minutes`;
		const h = Math.floor(minutes / 60);
		const m = minutes % 60;
		return m > 0
			? `${h} hour${h > 1 ? "s" : ""} ${m} min`
			: `${h} hour${h > 1 ? "s" : ""}`;
	};

	return (
		<Accordion expanded={expanded} onChange={handleExpand}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="subtitle1" fontWeight="bold">
					Mobile App Configuration
				</Typography>
				{mobileMetadata?.notificationTitle && (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ ml: 2, alignSelf: "center" }}
					>
						Configured
					</Typography>
				)}
			</AccordionSummary>
			<AccordionDetails>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
					{/* Notification Title */}
					<Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="body2" fontWeight="bold">
								Notification Title
							</Typography>
							<InsertVariableButton
								inputRef={titleRef as any}
								value={metadata.notificationTitle}
								onChange={(v) => updateField("notificationTitle", v)}
							/>
						</Box>
						<CharacterCounterField
							label="Notification Title"
							value={metadata.notificationTitle}
							onChange={(v) => updateField("notificationTitle", v)}
							maxLength={65}
							required
							inputRef={titleRef as any}
							helperText="Push notification title shown to the beneficiary"
						/>
						{metadata.notificationTitle && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Preview: {replaceTemplateVars(metadata.notificationTitle)}
							</Typography>
						)}
					</Box>

					{/* Notification Body */}
					<Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="body2" fontWeight="bold">
								Notification Body
							</Typography>
							<InsertVariableButton
								inputRef={bodyRef as any}
								value={metadata.notificationBody}
								onChange={(v) => updateField("notificationBody", v)}
							/>
						</Box>
						<CharacterCounterField
							label="Notification Body"
							value={metadata.notificationBody}
							onChange={(v) => updateField("notificationBody", v)}
							maxLength={240}
							required
							multiline
							rows={3}
							inputRef={bodyRef as any}
							helperText="Push notification body text"
						/>
						{metadata.notificationBody && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Preview: {replaceTemplateVars(metadata.notificationBody)}
							</Typography>
						)}
					</Box>

					{/* Instructions Text */}
					<Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="body2" fontWeight="bold">
								Instructions Text (Optional)
							</Typography>
							<InsertVariableButton
								inputRef={instructionsRef as any}
								value={metadata.instructionsText ?? ""}
								onChange={(v) => updateField("instructionsText", v)}
							/>
						</Box>
						<CharacterCounterField
							label="Instructions Text"
							value={metadata.instructionsText ?? ""}
							onChange={(v) => updateField("instructionsText", v)}
							maxLength={300}
							multiline
							rows={5}
							inputRef={instructionsRef as any}
							helperText="Instructions shown in the app before completing the event"
						/>
					</Box>

					{/* Success Message */}
					<Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="body2" fontWeight="bold">
								Success Message
							</Typography>
							<InsertVariableButton
								inputRef={successRef as any}
								value={metadata.successMessage}
								onChange={(v) => updateField("successMessage", v)}
							/>
						</Box>
						<CharacterCounterField
							label="Success Message"
							value={metadata.successMessage}
							onChange={(v) => updateField("successMessage", v)}
							maxLength={100}
							required
							inputRef={successRef as any}
							helperText="Message shown after successful event completion"
						/>
						{metadata.successMessage && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Preview: {replaceTemplateVars(metadata.successMessage)}
							</Typography>
						)}
					</Box>

					{/* Reminder Notifications */}
					<Accordion
						sx={{ mt: 1 }}
						expanded={remindersEnabled}
						onChange={() => {
							/* controlled by checkbox */
						}}
					>
						<AccordionSummary>
							<FormControlLabel
								control={
									<Checkbox
										checked={remindersEnabled}
										onChange={(e) => {
											e.stopPropagation();
											toggleReminders(e.target.checked);
										}}
										onClick={(e) => e.stopPropagation()}
									/>
								}
								label={
									<Typography variant="subtitle2" fontWeight="bold">
										Enable Reminder Notifications
									</Typography>
								}
								onClick={(e) => e.stopPropagation()}
							/>
						</AccordionSummary>
						<AccordionDetails>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								{reminders.map((reminder, index) => (
									<Paper
										key={index}
										sx={{ p: 2, bgcolor: "grey.50", position: "relative" }}
									>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1.5,
											}}
										>
											<Typography variant="body2" fontWeight="bold">
												Reminder {index + 1}
											</Typography>
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 1 }}
											>
												<Typography variant="caption" color="primary">
													{getCumulativeTiming(index)}
												</Typography>
												<IconButton
													size="small"
													onClick={() => removeReminder(index)}
													disabled={reminders.length <= 1}
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Box>
										</Box>

										<Box
											sx={{
												display: "flex",
												gap: 1,
												mb: 1.5,
												alignItems: "center",
											}}
										>
											<TextField
												label="Interval"
												type="number"
												size="small"
												value={
													reminder.intervalMinutes >= 60
														? reminder.intervalMinutes / 60
														: reminder.intervalMinutes
												}
												onChange={(e) => {
													const raw = Number(e.target.value);
													const isHours = reminder.intervalMinutes >= 60;
													updateReminder(index, {
														intervalMinutes: isHours
															? Math.round(raw * 60)
															: raw,
													});
												}}
												inputProps={{ min: 1 }}
												sx={{ width: 120 }}
											/>
											<FormControl size="small" sx={{ minWidth: 110 }}>
												<InputLabel>Unit</InputLabel>
												<Select
													value={
														reminder.intervalMinutes >= 60 ? "hours" : "minutes"
													}
													label="Unit"
													onChange={(e) => {
														const unit = e.target.value;
														const current = reminder.intervalMinutes;
														if (unit === "hours" && current < 60) {
															updateReminder(index, {
																intervalMinutes: current * 60,
															});
														} else if (unit === "minutes" && current >= 60) {
															updateReminder(index, {
																intervalMinutes: Math.round(current / 60),
															});
														}
													}}
												>
													<MenuItem value="minutes">Minutes</MenuItem>
													<MenuItem value="hours">Hours</MenuItem>
												</Select>
											</FormControl>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ ml: 1 }}
											>
												({formatMinutesDisplay(reminder.intervalMinutes)})
											</Typography>
										</Box>

										<Box sx={{ position: "relative" }}>
											<TextField
												fullWidth
												label="Reminder Message"
												value={reminder.message}
												onChange={(e) =>
													updateReminder(index, { message: e.target.value })
												}
												multiline
												rows={2}
												inputProps={{ maxLength: 240 }}
											/>
											<Box sx={{ position: "absolute", right: 10, bottom: 10 }}>
												<Typography variant="caption" color="text.secondary">
													{reminder.message.length}/240
												</Typography>
											</Box>
										</Box>
									</Paper>
								))}

								<Button
									startIcon={<AddIcon />}
									onClick={addReminder}
									disabled={reminders.length >= MAX_REMINDERS}
									variant="outlined"
									size="small"
									sx={{ alignSelf: "flex-start" }}
								>
									Add Reminder ({reminders.length}/{MAX_REMINDERS})
								</Button>
							</Box>
						</AccordionDetails>
					</Accordion>
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};

export default MobileMetadataSection;
